/**
 * Kopfzeile der Projektseite: Zurück, Titel, Kunde · Ort · Art · verantwortlich
 * und „⋯" mit allem, was selten gebraucht wird (Plan Projekt-Tracking, Etappe 2).
 *
 * Den Status schreibt die Phase mit (Etappe 1). Von Hand bleibt die Auswahl im
 * Menü (Benjamin, 03.10.2026: im Stil der App) — gebraucht wird sie vor allem für
 * „verloren", „abgeschlossen" und das Wiederöffnen. Der Statusvorschlag der
 * Auftragslage ist seit Etappe 9 weg: der Kopf nennt Phase und Schritt selbst.
 * „Pausieren" fehlt bewusst: den Status gibt es auf dem Server noch nicht.
 *
 * Seit 26.10.2026 stehen hier auch „Kein Einsatz nötig — direkt abrechnen" und
 * „Vor Ort bar bezahlt" (vorher Textlinks unter dem Hauptknopf der Karte „Jetzt").
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";

import { useDeleteProjekt, useProjektStatusOptions, useSetProjektStatus } from "../../hooks/usePlanung";
import { useNacharbeit } from "../../hooks/useProjektLage";
import { barMoeglich, ohneEinsatzMoeglich, type ProjektLage } from "../../lib/projektLage";
import { calendarYearNow, statusTon, statusWort } from "../../lib/projekte";
import type { ProjektDetail } from "../../lib/types";
import StatusZurueckDialog from "./StatusZurueckDialog";
import { useIstTelefon } from "../../hooks/useIstTelefon";

type Props = {
  projektId: string;
  d: ProjektDetail | undefined;
  /** Phase des Projekts — entscheidet, ob „Kein Einsatz nötig" und „Vor Ort bar bezahlt" im Menü stehen. */
  lage?: ProjektLage;
  onStammdaten: () => void;
  onBarBezahlt: () => void;
};

export default function KopfZeile({ projektId, d, lage, onStammdaten, onBarBezahlt }: Props) {
  const navigate = useNavigate();
  const telefon = useIstTelefon();
  const [offen, setOffen] = useState(false);
  const [loeschen, setLoeschen] = useState(false);
  const menue = useRef<HTMLDivElement>(null);
  const { data: statusOpts = [] } = useProjektStatusOptions();
  const setStatus = useSetProjektStatus(calendarYearNow());
  const del = useDeleteProjekt();
  const nacharbeit = useNacharbeit(projektId);
  const ohneEinsatz = !!lage && ohneEinsatzMoeglich(lage);
  const bar = !!lage && barMoeglich(lage) && !((lage.geld.bar_brutto ?? 0) > 0);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !menue.current?.contains(e.target as Node)) {
        setOffen(false);
        setLoeschen(false);
      }
    };
    document.addEventListener("mousedown", zu);
    document.addEventListener("keydown", zu);
    return () => { document.removeEventListener("mousedown", zu); document.removeEventListener("keydown", zu); };
  }, [offen]);

  const status = d?.status || "";
  const verloren = statusOpts.find((s) => s.includes("verloren")) || "";
  const [zurueck, setZurueck] = useState(false);
  // Von beauftragt zurück auf Anfrage geht über die Rückfrage (12.10.2026) — sonst
  // rechnet die Phase aus dem angenommenen Angebot gleich wieder „aktiv".
  const istZurueck = (neu: string) => neu.includes("anfrage") && /aktiv|beauftragt|rechnung/.test(status);
  const statusSetzen = (neu: string) => {
    if (istZurueck(neu)) { setOffen(false); setZurueck(true); return; }
    setStatus.mutate({ id: projektId, status: neu }, { onSuccess: () => setOffen(false) });
  };
  const angebotLink = `/rechnungen/neu?kind=quote&projekt=${projektId}${d?.kunde_id ? `&kunde=${d.kunde_id}` : ""}`;
  const unterzeile = [
    d?.kunde_name || "ohne Kunde",
    d?.ort,
    d?.projektart ? (d.projektart === "pflege" ? "Pflegeprojekt" : "Bauprojekt") : "",
    d?.verantwortlicher,
  ].filter(Boolean).join(" · ");

  return (
    <header className="pk-zeile">
      <button type="button" className="pk-zurueck" aria-label="Zurück zu Projekte" onClick={() => navigate("/projekte")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        <span className="pk-zurueck-text">Projekte</span>
      </button>
      <div className="pk-titelblock">
        <h1 className="pk-titel">{d?.title || "Projekt"}</h1>
        <p className="pk-unter">{unterzeile}</p>
      </div>
      <div className="pk-menue" ref={menue}>
        <button type="button" className="pk-rund" aria-label="Weitere Aktionen zum Projekt" aria-haspopup="menu" aria-expanded={offen} onClick={() => setOffen((v) => !v)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
        </button>
        {offen && (
          <div className="pk-menue-liste" role="menu">
            {!telefon && <Link role="menuitem" className="pk-menue-punkt" to={angebotLink}>Neues Angebot</Link>}
            <button type="button" role="menuitem" className="pk-menue-punkt" onClick={() => { setOffen(false); onStammdaten(); }}>Stammdaten bearbeiten</button>
            {ohneEinsatz && (
              <button type="button" role="menuitem" className="pk-menue-punkt" disabled={nacharbeit.isPending}
                onClick={() => nacharbeit.mutate(false, { onSuccess: () => setOffen(false) })}>
                {nacharbeit.isPending ? "Wird eingetragen …" : "Ohne Einsatz direkt abrechnen"}
              </button>
            )}
            {bar && (
              <button type="button" role="menuitem" className="pk-menue-punkt" onClick={() => { setOffen(false); onBarBezahlt(); }}>
                Vor Ort bar bezahlt — ohne Rechnung abschließen
              </button>
            )}
            {verloren && !status.includes("verloren") && (
              <button type="button" role="menuitem" className="pk-menue-punkt" disabled={setStatus.isPending} onClick={() => statusSetzen(verloren)}>
                Als verloren markieren
              </button>
            )}
            <div className="pk-menue-status" role="group" aria-label="Status von Hand">
              <span>Status von Hand</span>
              <small className="pk-status-hinweis">Die Phase schreibt ihn mit — von Hand meist nur „verloren“ oder „abgeschlossen“. „Anfrage“ fragt nach, was mit Angeboten und Aufgaben passiert.</small>
              <div className="pk-status-wahl">
                {statusOpts.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="menuitemradio"
                    aria-checked={s === status}
                    className={"pk-status-knopf" + (s === status ? " is-aktiv" : "")}
                    disabled={setStatus.isPending || s === status}
                    onClick={() => statusSetzen(s)}
                  >
                    <span className={`pk-status-punkt is-${statusTon(s)}`} aria-hidden="true" />
                    {statusWort(s)}
                  </button>
                ))}
              </div>
            </div>
            <div className="pk-menue-trenner" />
            {!loeschen ? (
              <button type="button" role="menuitem" className="pk-menue-punkt is-gefahr" onClick={() => setLoeschen(true)}>Projekt löschen</button>
            ) : (
              <div className="pk-menue-loeschen">
                <span>Sicher? Termine werden mit gelöscht.</span>
                <button type="button" className="vg-btn-danger" disabled={del.isPending}
                  onClick={() => del.mutate(projektId, { onSuccess: () => navigate("/projekte") })}>
                  {del.isPending ? "…" : "Ja, löschen"}
                </button>
              </div>
            )}
            {(setStatus.error || del.error || nacharbeit.error) && <p className="pk-fehler">{fehlerText(setStatus.error || del.error || nacharbeit.error)}</p>}
          </div>
        )}
      </div>
      {zurueck && <StatusZurueckDialog projektId={projektId} titel={d?.title} onClose={() => setZurueck(false)} />}
    </header>
  );
}
